import {
  IncomingSparkInvitation,
  PastSparkForUser,
  SparkParticipantStatus,
  UpcomingSparkForUser,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { StoreSchema } from "./StoreSchema";
import { apiReq } from "../apiReq";
import { LOGIN_TOKEN_KEY } from "./store";
import posthog from "posthog-js";
import { isLoggedInPage } from "../isLoggedInPage";
import { Capacitor } from "@capacitor/core";

function identifyUser(user: UserWithMediaAndGroups) {
  posthog.identify(user.id.toString(), {
    email: user.email,
    first_name: user.first_name,
    gender: user.gender,
    gender_preference: user.gender_preference,
  });
}

export enum ActionType {
  SetLoading = "SetLoading",
  UpdateAuth = "UpdateAuth",
  Login = "Login",
  LogOut = "LogOut",
  UpdateUser = "UpdateUser",
  UpdateUserState = "UpdateUserState",
  UpdateParticipantStatus = "UpdateParticipantStatus",
  UpdateMatchesCompleted = "UpdateMatchesCompleted",
}

interface ActionMeta {
  [ActionType.SetLoading]: {
    params: {
      loading: boolean;
    };
    return: void;
  };
  [ActionType.UpdateAuth]: {
    params: Partial<StoreSchema["auth"]>;
    return: void;
  };
  [ActionType.Login]: {
    params: {
      token: string;
      user: UserWithMediaAndGroups;
      upcomingSparks?: UpcomingSparkForUser[];
      pastSparks?: PastSparkForUser[];
      incomingInvitations?: IncomingSparkInvitation[];
    };
    return: void;
  };
  [ActionType.LogOut]: {
    params: Record<string, never>;
    return: void;
  };
  [ActionType.UpdateUser]: {
    params: {
      user?: UserWithMediaAndGroups;
    };
    return: void;
  };
  [ActionType.UpdateUserState]: {
    params: {
      user?: UserWithMediaAndGroups;
      upcomingSparks?: UpcomingSparkForUser[];
      pastSparks?: PastSparkForUser[];
      incomingInvitations?: IncomingSparkInvitation[];
    };
    return: void;
  };
  [ActionType.UpdateParticipantStatus]: {
    params: {
      sparkId: number;
      userId: number;
      status: SparkParticipantStatus | null;
    };
    return: void;
  };
  [ActionType.UpdateMatchesCompleted]: {
    params: {
      sparkId: number;
    };
    return: void;
  };
}

export const handlers: ActionHandlers = {
  [ActionType.SetLoading](params, store) {
    store.loading = params.loading;
  },
  [ActionType.UpdateAuth](params, store) {
    store.auth = { ...store.auth, ...params };
  },
  [ActionType.Login](params, store) {
    store.auth.token = params.token;
    store.user = { ...params.user };
    if (params.upcomingSparks) store.upcomingSparks = params.upcomingSparks;
    if (params.pastSparks) store.pastSparks = params.pastSparks;
    if (params.incomingInvitations)
      store.incomingInvitations = params.incomingInvitations;
    apiReq.headers.authorization = `Bearer ${params.token}`;
    localStorage.setItem(LOGIN_TOKEN_KEY, params.token);
    identifyUser(params.user);
  },
  [ActionType.LogOut](_params, store) {
    store.auth = {};
    store.user = undefined;
    store.upcomingSparks = undefined;
    store.pastSparks = undefined;
    store.incomingInvitations = undefined;
    delete apiReq.headers.authorization;
    localStorage.removeItem(LOGIN_TOKEN_KEY);
    posthog.reset();
    if (Capacitor.isNativePlatform()) {
      window.location.href = "/app/logged-out";
    } else if (isLoggedInPage(window.location.pathname)) {
      window.location.href = "/events";
    }
  },
  [ActionType.UpdateUser](params, store) {
    store.user = params.user;
    if (params.user) identifyUser(params.user);
  },
  [ActionType.UpdateUserState](params, store) {
    store.user = params.user;
    store.upcomingSparks = params.upcomingSparks;
    store.pastSparks = params.pastSparks;
    store.incomingInvitations = params.incomingInvitations;
    if (params.user) identifyUser(params.user);
  },
  [ActionType.UpdateParticipantStatus](params, store) {
    const spark = store.pastSparks?.find((s) => s.id === params.sparkId);
    if (!spark) return;
    const participant = spark.participants.find((p) => p.id === params.userId);
    if (!participant) return;
    participant.status = params.status;
  },
  [ActionType.UpdateMatchesCompleted](params, store) {
    const spark = store.pastSparks?.find((s) => s.id === params.sparkId);
    if (!spark) return;
    spark.matchesCompleted = true;
  },
};

export interface GenericAction<T extends ActionType> {
  type: T;
  params: ActionMeta[T]["params"];
}

type GenericHandler<T extends ActionType> = (
  params: ActionMeta[T]["params"],
  store: StoreSchema
) => ActionMeta[T]["return"];

export type Action = {
  [K in keyof typeof ActionType]: GenericAction<(typeof ActionType)[K]>;
}[keyof typeof ActionType];

type ActionHandlers = {
  [K in ActionType]: GenericHandler<K>;
};
